// Google Drive API Service
// Note: This requires OAuth2 setup with Google Cloud Console

interface DriveConfig {
  clientId: string;
  apiKey: string;
  folderId?: string; // Optional specific folder ID
}

export class GoogleDriveService {
  private static instance: GoogleDriveService;
  private accessToken: string | null = null;
  private config: DriveConfig | null = null;

  // Diagnostic function to check which environment variables are missing
  static checkEnvironmentVariables(): {
    status: 'success' | 'error';
    missing: string[];
    configured: string[];
    details: Record<string, { present: boolean; value?: string }>;
  } {
    const envVars = {
      VITE_GOOGLE_CLIENT_ID: import.meta.env.VITE_GOOGLE_CLIENT_ID,
      VITE_GOOGLE_API_KEY: import.meta.env.VITE_GOOGLE_API_KEY,
      VITE_GOOGLE_DRIVE_FOLDER_ID: import.meta.env.VITE_GOOGLE_DRIVE_FOLDER_ID
    };

    const missing: string[] = [];
    const configured: string[] = [];
    const details: Record<string, { present: boolean; value?: string }> = {};

    for (const [key, value] of Object.entries(envVars)) {
      const isPresent = value && value !== '' && value !== undefined;
      details[key] = {
        present: isPresent,
        // Only show first/last 4 chars for security
        value: isPresent ? `${String(value).slice(0, 4)}...${String(value).slice(-4)}` : undefined
      };

      if (isPresent) {
        configured.push(key);
      } else if (key !== 'VITE_GOOGLE_DRIVE_FOLDER_ID') { // Folder ID is optional
        missing.push(key);
      }
    }

    return {
      status: missing.length === 0 ? 'success' : 'error',
      missing,
      configured,
      details
    };
  }

  // Initialize Google API with detailed error reporting
  static async initialize() {
    const envCheck = this.checkEnvironmentVariables();
    
    const config: DriveConfig = {
      clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
      apiKey: import.meta.env.VITE_GOOGLE_API_KEY || '',
      folderId: import.meta.env.VITE_GOOGLE_DRIVE_FOLDER_ID || ''
    };

    if (!config.clientId || !config.apiKey) {
      console.error('Google Drive API Configuration Error:', {
        status: envCheck.status,
        missing: envCheck.missing,
        configured: envCheck.configured,
        details: envCheck.details,
        message: `Missing required environment variables: ${envCheck.missing.join(', ')}`
      });
      console.warn('Using local download fallback. Please configure the following environment variables in Cloudflare:');
      envCheck.missing.forEach(varName => {
        console.warn(`  - ${varName}`);
      });
      return null;
    }

    if (!this.instance) {
      this.instance = new GoogleDriveService();
      this.instance.config = config;
      console.log('Google Drive Service initialized successfully with:', {
        configured: envCheck.configured,
        hasFolderId: !!config.folderId
      });
    }

    return this.instance;
  }

  // OAuth2 Authentication
  async authenticate(): Promise<boolean> {
    try {
      // Use Google Identity Services for OAuth2
      const tokenClient = (window as any).google?.accounts?.oauth2?.initTokenClient({
        client_id: this.config.clientId,
        scope: 'https://www.googleapis.com/auth/drive.file',
        callback: (response: any) => {
          if (response.access_token) {
            this.accessToken = response.access_token;
            console.log('Successfully authenticated with Google Drive');
          }
        },
      });

      if (!tokenClient) {
        console.error('Google Identity Services not loaded');
        return false;
      }

      // Request access token
      return new Promise((resolve) => {
        const originalCallback = tokenClient.callback;
        tokenClient.callback = (response: any) => {
          originalCallback(response);
          resolve(!!response.access_token);
        };
        tokenClient.requestAccessToken();
      });
    } catch (error) {
      console.error('Google Drive authentication failed:', error);
      return false;
    }
  }

  // Upload file to Google Drive
  async uploadFile(fileName: string, content: string, mimeType: string = 'text/html'): Promise<boolean> {
    // Try to use existing token first
    if (!this.accessToken) {
      console.log('No access token, attempting authentication...');
      const authenticated = await this.authenticate();
      if (!authenticated) {
        console.log('Authentication failed, using local download fallback');
        return false;
      }
    }

    try {
      const metadata = {
        name: fileName,
        mimeType: mimeType,
        parents: this.config?.folderId ? [this.config.folderId] : []
      };

      const formData = new FormData();
      formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      formData.append('file', new Blob([content], { type: mimeType }));

      const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        },
        body: formData
      });

      return response.ok;
    } catch (error) {
      console.error('Failed to upload to Google Drive:', error);
      return false;
    }
  }

  // Simple integration guide for users
  static getSetupInstructions(): string {
    return `
Google Drive 직접 저장 설정 방법:

1. Google Cloud Console에서 프로젝트 생성
2. Google Drive API 활성화
3. OAuth 2.0 클라이언트 ID 생성
4. 환경 변수 설정:
   - VITE_GOOGLE_CLIENT_ID: OAuth 클라이언트 ID
   - VITE_GOOGLE_API_KEY: API 키
   - VITE_GOOGLE_DRIVE_FOLDER_ID: (선택) 저장할 폴더 ID

현재는 로컬 다운로드로 작동하며, 다운로드된 파일을 
Google Drive에 수동으로 업로드할 수 있습니다.
    `;
  }
}